// =============================================================================
// Terms.js, /terms (Terms of Service)
//
// Phase 38.7, Pre-incorporation rewrite:
//   • Operator: "2120" (in private beta, operated from Bangalore, India)
//   • Governing law: Republic of India; courts at Bangalore (Karnataka)
//   • Contact: privacy@2120.money
//
// This is starter SaaS terms tuned for an Indian operator serving global
// users with dual-currency billing (INR via Razorpay, USD via Stripe).
// =============================================================================
import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

export default function Terms() {
  usePageTitle('Terms of Service');
  const sections = [
    {
      heading: '1. Acceptance of these Terms',
      body: 'These Terms of Service (the “Terms”) form a binding agreement between you and 2120 (“2120”, “we”, “us”), and govern your access to and use of the 2120 web application, websites, and related services (collectively, the “Service”). 2120 is currently operated by its founding team in Bangalore, India; a formal legal entity will be designated before commercial general availability.\n\nBy creating an account, signing in, or otherwise using the Service, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree, please do not use the Service.',
    },
    {
      heading: '2. Eligibility',
      body: 'You must be at least 18 years old and capable of entering into a legally binding contract in your jurisdiction to use the Service. By using 2120, you represent and warrant that you meet these eligibility requirements.',
    },
    {
      heading: '3. Your account',
      body: 'When you create an account, you agree to provide accurate, current, and complete information and to keep it up to date. You are responsible for safeguarding your password and for any activity under your account. Notify us immediately at privacy@2120.money of any unauthorised access.',
    },
    {
      heading: '4. Our Service',
      body: '2120 is a personal financial clarity tool. It helps you reflect on income, expenses, goals, and net worth across multiple currencies. It is not financial, investment, tax, or legal advice. Any insights, projections, or summaries we generate, including those produced with AI, are informational only. You remain solely responsible for your financial decisions.',
    },
    {
      heading: '5. Plans, billing, and trials',
      body: '2120 offers a free tier and paid subscription plans (currently Clarity, Horizon, and Summit). Pricing, features, and limits are displayed on our Pricing page. Paid subscriptions renew automatically at the end of each billing cycle (monthly or annual) until cancelled.\n\nWe offer a 14-day free trial on Horizon and Summit plans. Trials require no payment method; if you do not subscribe before the trial ends, your account returns to the Free plan and your data is preserved. Cancellation takes effect at the end of your then-current billing cycle, you keep access until the period you have already paid for ends. Applicable taxes (such as GST under Indian law) may be charged at checkout.',
    },
    {
      heading: '6. Refunds',
      body: '2120 does not issue refunds for partial billing periods. When you cancel, your subscription remains active through the end of the cycle you have already paid for, and you are not charged again. Full details are in our Refund Policy.',
    },
    {
      heading: '7. Acceptable use',
      body: 'You agree to use the Service responsibly. The complete Acceptable Use Policy applies and is incorporated by reference. In summary, you will not:',
      list: [
        'Attempt to reverse engineer, decompile, or extract source code from the Service.',
        'Use the Service to store or transmit any unlawful, infringing, harassing, or fraudulent content.',
        'Interfere with or disrupt the Service or the servers and networks that host it.',
        'Probe, scan, or test the vulnerability of the Service except as expressly permitted in writing.',
        'Use automated means (bots, scrapers) to access the Service in a way that imposes an unreasonable load.',
      ],
    },
    {
      heading: '8. Your content and data',
      body: 'You retain ownership of the financial data, notes, and uploads you put into 2120 (your “Content”). You grant us a limited, worldwide, non-exclusive licence to store, process, and display your Content solely to provide the Service to you and the workspace partners you explicitly invite. We will never sell your Content or use it for advertising. Aggregated, anonymised, or de-identified data may be used to improve product quality.',
    },
    {
      heading: '9. Privacy',
      body: 'Our handling of personal data is described in our Privacy Policy, which is part of these Terms. Please review it carefully.',
    },
    {
      heading: '10. Disclaimers and limitation of liability',
      body: 'The Service is provided “as is” and “as available”. To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted or error-free, or that any insight or projection it produces will be accurate.\n\nTo the maximum extent permitted by law, our total liability under these Terms in any 12-month period will not exceed the greater of (a) the amount you paid us in that period or (b) INR 8,000 (or its USD equivalent of approximately USD 100). We are not liable for any indirect, incidental, special, consequential, or punitive damages.',
    },
    {
      heading: '11. Governing law and disputes',
      body: 'These Terms are governed by the laws of the Republic of India, without regard to its conflict-of-laws principles. Subject to mandatory consumer-protection law in your country of residence, any dispute arising under these Terms will be resolved exclusively in the competent courts at Bangalore, Karnataka, India.',
    },
    {
      heading: '12. Changes to the Service or these Terms',
      body: 'We may update the Service and these Terms from time to time. If a change is material, we will provide reasonable notice (for example, by email or in-app banner) before it takes effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.',
    },
    {
      heading: '13. Termination',
      body: 'You may stop using the Service at any time. We may suspend or terminate your access if we reasonably believe you have violated these Terms or applicable law, or if continued provision would create a security or legal risk. On termination, your right to use the Service ends, but sections that should reasonably survive (intellectual property, disclaimers, limitation of liability, governing law) continue to apply.',
    },
    {
      heading: '14. Contact',
      body: 'For questions about these Terms, please email privacy@2120.money.',
    },
  ];

  return (
    <LegalPage
      title="Terms of Service"
      subtitle="The rules of the road when you use 2120. Plain language wherever we could keep it that way."
      lastUpdated="May 31, 2026"
      sections={sections}
    />
  );
}
